import clsx from 'clsx';
import type { FC } from 'react';
import Plus from '@themaven-net/raven-assets/Plus';
import { Label } from '@/components/raven/ui/Label';
import { Button } from '@/components/raven/ui/Button';
import { Tooltip } from '@/components/raven/ui/Tooltip';
import Checkmark from '@themaven-net/raven-assets/Checkmark';

import styles from './styles.module.css';
import { FollowingState, type FollowButtonComponentProps } from './types';

const toolTipText: { [key in FollowingState]: string } = {
	// Logged In and Following
	[FollowingState.Active]: 'Click to stop receiving updates',
	// Logged In and Not Following
	[FollowingState.Inactive]: 'Click to receive email updates',
	// Logged out
	[FollowingState.LoggedOut]: 'Sign In to receive email updates',
	// Logged In and Not Following
	[FollowingState.Naive]: 'Click to receive email updates',
	// Pre-verification
	[FollowingState.Unknown]: '',
};

const buttonText: { [key in FollowingState]: string } = {
	[FollowingState.Active]: 'Following',
	[FollowingState.Inactive]: 'Follow',
	[FollowingState.LoggedOut]: 'Follow',
	[FollowingState.Naive]: 'Follow',
	[FollowingState.Unknown]: '',
};

export const FollowButtonComponent: FC<FollowButtonComponentProps> = ({
	className,
	containerClassName,
	followingState,
	id,
	labelProps,
	onClick,
	variant,
}) => {
	// Null button to avoid displaying "Log In To Follow" during the
	// initial lookup if the user *is* logged in.
	if (followingState === FollowingState.Unknown && variant !== 'label') {
		return null;
	}
	const FollowButtonIcon =
		followingState === FollowingState.Active ? Checkmark : Plus;

	return (
		<div className={className}>
			<Tooltip content={toolTipText[followingState]}>
				{variant !== 'label' && (
					<div
						className={clsx(styles.followButton, containerClassName, {
							[styles.stackedFollowButton]: variant === 'stacked',
						})}
					>
						<label htmlFor={id}>{buttonText[followingState]}</label>
						<Button
							id={id}
							label="follow-button"
							onClick={onClick}
							type="button"
							variation="icon-large"
						>
							<FollowButtonIcon />
						</Button>
					</div>
				)}
				{variant === 'label' && labelProps && (
					<button
						className={clsx(styles.followButtonLabel, {
							[styles.followButtonActive]:
								followingState === FollowingState.Active,
							[styles.followButtonInactive]:
								followingState !== FollowingState.Active,
						})}
						onClick={onClick}
						type="button"
					>
						<Label
							followProps={{
								icon:
									followingState === FollowingState.Unknown ? (
										''
									) : (
										<FollowButtonIcon />
									),
								text: buttonText[followingState],
							}}
							media={labelProps.media}
							text={labelProps.text}
							variant="follow"
						/>
					</button>
				)}
			</Tooltip>
		</div>
	);
};
